import { CanvasToggleButton } from "@/components/layout/CanvasToggleButton.tsx";
import {
  SplitPanelHeader,
  panelHeaderControlClass,
  panelHeaderCrumbTitleClass,
  panelTrailingIconClass,
} from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import {
  type PanelHeaderAction,
  PanelHeaderActionButton,
  PanelHeaderMenu,
  PanelHeaderMenuItem,
  splitPanelHeaderActions,
} from "@/components/layout/SplitPanel/panelHeaderActions.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogClose,
  DialogContentContained,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { cn } from "@/lib/utils.ts";
import { HeaderUtilitySlot } from "@/components/layout/HeaderUtilitySlot.tsx";
import { useCanvasHeaderUtilityLayer } from "./canvasContext.tsx";
import { ChevronLeft, ChevronRight, Maximize2, Minimize2, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { type ReactElement, type ReactNode, useRef } from "react";
import { useCanvasActionContext, useCanvasDismissKind } from "./canvasContext.tsx";
import { isTypingOutside } from "./canvasFocus.ts";

/** Centered muted body used by canvas-card loading, error, and empty states. */
export function CanvasCenteredBody({ children }: { children: ReactNode }): ReactElement {
  return (
    <div className="flex flex-1 min-h-0 items-center justify-center p-6 text-muted-foreground text-sm">
      {children}
    </div>
  );
}

type FullscreenToggleProps = {
  isFullscreen: boolean;
  onToggle: () => void;
};

/** The display-mode toggle shown in canvas card headers. */
function FullscreenToggle({ isFullscreen, onToggle }: FullscreenToggleProps): ReactElement {
  const Icon = isFullscreen ? Minimize2 : Maximize2;
  return (
    <Button
      variant="ghost"
      size="icon"
      className={panelHeaderControlClass}
      aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
      data-statsig-name="canvas-modal-fullscreen"
      onClick={onToggle}
    >
      <Icon />
    </Button>
  );
}

/** Position of this card within a multi-artifact review queue. */
export type CanvasCardPagination = {
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  /** Commit what the review staged, leaving undecided cards pending. Replaces the
   *  next chevron on the last card, where there is nothing left to advance to. */
  onSkip: () => void;
};

/**
 * Carousel controls for a card that is one of several under review, mirroring the
 * biscuit's pager: counter, prev only once there is a card behind it, and on
 * the last
 * card — the next chevron replaced by "Skip", the queue's single commit. Paging
 * itself never commits, so moving between cards can't submit the review out from
 * under the user.
 */
function CanvasPager({ index, total, onPrev, onNext, onSkip }: CanvasCardPagination): ReactElement {
  const isLast = index === total - 1;
  return (
    <div className="flex items-center gap-1">
      <span className="text-muted-foreground text-xs tabular-nums">
        {index + 1} of {total}
      </span>
      {/* Drawn only where there is a card behind it. Unlike `ArtifactBiscuit`'s
          this group leads with its counter, so prev sits in the middle and
          removing it outright would drag the counter sideways on every step
          between the first card and the second. The slot is held instead, which
          costs the space but not the movement. */}
      {index > 0 ? (
        <Button
          variant="ghost"
          size="icon"
          className={panelHeaderControlClass}
          aria-label="Previous artifact"
          data-statsig-name="canvas-modal-prev"
          onClick={onPrev}
        >
          <ChevronLeft />
        </Button>
      ) : (
        // Same class as the control it stands in for, so the held slot tracks
        // the chrome scale by construction rather than by a copied number.
        <span className={cn(panelHeaderControlClass, "shrink-0")} aria-hidden />
      )}
      {isLast ? (
        <Button
          variant="secondary"
          size="xs"
          onClick={onSkip}
          data-statsig-name="canvas-modal-skip"
        >
          Skip
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          className={panelHeaderControlClass}
          aria-label="Next artifact"
          data-statsig-name="canvas-modal-next"
          onClick={onNext}
        >
          <ChevronRight />
        </Button>
      )}
    </div>
  );
}

/**
 * The generic canvas card: the presentational frame that sources, artifacts, and
 * contacts render into as one layer of the modal stack. It provides a header
 * (optional leading Back + optional icon + title + close), a scrollable body, and
 * an optional footer bar. The stack ({@link CanvasStack}) owns layer
 * visibility and supplies a layer-local portal target; the shared contained
 * dialog owns the scrim and reports dismissals through `onClose`.
 *
 * Non-modal by design (`aria-modal="false"`): the surrounding chat stays live, so
 * there is no focus trap or scroll lock. Radix owns dialog semantics, initial
 * focus, Escape, and nested-layer precedence. A lower layer revealed by a pop
 * does not re-animate because it never unmounts.
 */
export const CanvasModalCard = ({
  title,
  titleClassName,
  titleText,
  icon: Icon,
  footer,
  isFullscreen = false,
  bodyFills = false,
  pageBackground = false,
  pagination,
  headerActions,
  headerExtras,
  headerGrows = false,
  children,
  container,
  showBack = false,
  onBack,
  onClose,
}: {
  title: ReactNode;
  /**
   * Extra layout for the title element, merged after the shared crumb-title
   * treatment. That treatment already truncates to one line, so this is only
   * for a title that has to grow — an inline editor, or a portal target the
   * owning panel fills.
   */
  titleClassName?: string;
  /**
   * The title as text, for places that need a string rather than the rendered
   * node — the overflow menu, which opens onto it. Falls back to `title` when
   * that is already a string; a card whose title is a node (a portal target, an
   * inline editor) has to supply it.
   */
  titleText?: string;
  /** Optional leading header icon (e.g. the artifact type icon). */
  icon?: LucideIcon;
  /**
   * Entity-specific header controls, placed left of the fullscreen toggle and
   * close so the frame's own affordances stay in the same spot on every card.
   *
   * These collect into an overflow menu rather than crowding the row, because
   * this column is narrow at every width. A card contributing one action keeps
   * it in the bar — a menu holding a single item is a wasted click.
   */
  headerActions?: PanelHeaderAction[];
  /**
   * Header controls that cannot become menu items — a picker or popover that
   * owns its own trigger. Rendered in the bar beside the frame's own controls.
   *
   * Prefer `headerActions`: anything expressible as icon-label-onClick belongs
   * there, where it can move out of the way when the row fills up.
   */
  headerExtras?: ReactNode;
  /**
   * Let the header row grow past the shared panel height when its own content
   * needs the room — an inline editor that soft-wraps, say.
   *
   * Off by default, and it should stay off unless a card genuinely has growing
   * header content: the fixed height is what keeps this row level with the
   * panel header beside it. A card that truncates its title never reaches the
   * floor, so opting in costs nothing at rest and only yields while editing.
   */
  headerGrows?: boolean;
  /**
   * Optional footer content. Sits at the end of the scrolling body rather than
   * in a bar beneath it, so a short body keeps its actions directly under the
   * last line instead of stranding them at the foot of the column.
   */
  footer?: ReactNode;
  /** Present the entity filling the canvas body rather than as a bounded card. */
  isFullscreen?: boolean;
  /**
   * The body is one surface that scrolls itself, such as an applet's frame, so
   * the card takes its full height and the body does not scroll. Scrolling it
   * too would stack a second scrollbar around the frame's own.
   */
  bodyFills?: boolean;
  /**
   * Paint the card the page's colour instead of white, for a body that brings
   * its own surfaces, such as an applet, whose page is the app's background.
   */
  pageBackground?: boolean;
  /** Carousel controls, present only while this card is one of several artifacts
   *  under review — the same queue the omnibox biscuit is paging through. */
  pagination?: CanvasCardPagination;
  children: ReactNode;
  /** This layer's wrapper, which scopes its card and scrim to the canvas. */
  container: HTMLElement;
  /** Show the leading Back affordance (only meaningful above the stack root). */
  showBack?: boolean;
  /** Pop this layer, revealing the one beneath. */
  onBack: () => void;
  /** Clear the whole stack, resting the calendar. */
  onClose: () => void;
}) => {
  // Absent wherever the card has no size to choose: a surface with no canvas
  // state to persist into (home, mobile), or a column the card fills, which has
  // no bounded size to shrink into. The toggle goes with the action rather than
  // rendering dead.
  const setDisplayMode = useCanvasActionContext()?.setDisplayMode;
  const dismissKind = useCanvasDismissKind();
  // Anchors the header measurement, and is the control the overflow collects into.
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  // The header's trailing group. Its last control is the dismiss, whatever that
  // is on this surface — the column's toggle where the card is the column, an X
  // where it is a dialog over something that stays.
  const contentRef = useRef<HTMLDivElement>(null);
  // No width measured: every action a card contributes is `never`-tier, so the
  // split is the same at every width and an observer on this header would
  // re-render the card on each frame of a divider drag for nothing.
  // Cards author their actions `never`-tier: this column is narrow at every
  // width it can be dragged to, so there is no width at which crowding the row
  // would be the right answer. The shared split still promotes a lone action
  // back into the bar, which is what keeps a one-item menu from costing a click.
  const { barActions, menuActions, soleAction, showMenu } = splitPanelHeaderActions({
    actions: headerActions ?? [],
  });
  const headerUtilityLayer = useCanvasHeaderUtilityLayer();

  return (
    <Dialog
      modal={false}
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContentContained
        ref={contentRef}
        container={container}
        fullscreen={isFullscreen}
        // The bounded card is only capped, so without a height a filling body
        // would collapse to its content.
        className={cn(
          bodyFills && !isFullscreen && "h-[calc(100%-2rem)]",
          pageBackground && "bg-background",
        )}
        // `showBack` means this layer sits above another, so the scrim it is
        // about to draw is already on screen underneath it. Animating in from
        // opacity 0 would take the backdrop down to nothing and build it back
        // up while the layer below is already hidden, which reads as a black
        // flash. The root layer has nothing beneath it and still fades in.
        animateEntrance={!showBack}
        aria-modal="false"
        aria-describedby={undefined}
        // Radix focuses the first focusable thing in the card, and every
        // candidate is a control on the header row. A tooltip opens on focus as
        // readily as on hover, so the card arrived with a label hanging over its
        // own header — for a control the reader was not pointing at, from a
        // gesture they never made.
        //
        // The card takes focus itself. Aiming at a particular control only moves
        // which label it is: the dismiss looks safe until the column-filling
        // card, whose dismiss is the canvas toggle and carries one too.
        //
        // Not at all while the user is typing elsewhere: the agent can open a
        // card mid-sentence, and the card must not take the sentence with it.
        onOpenAutoFocus={(event) => {
          if (!contentRef.current) return;
          event.preventDefault();
          if (isTypingOutside(container)) return;
          contentRef.current.focus();
        }}
      >
        {/* `bg-popover` rather than the shell's default `bg-background`: the card
            is a white surface, and a header is the colour of what it heads. Why
            sharing the shell is load-bearing rather than cosmetic — which panel
            headers this row has to stay level with — is in
            `web/docs/ui/canvas-system.md`. */}
        <SplitPanelHeader
          className={pageBackground ? "bg-background" : "bg-popover"}
          grows={headerGrows}
        >
          {showBack && (
            <Button
              variant="ghost"
              size="icon"
              className={cn(panelHeaderControlClass, "-ml-1.5 mr-1")}
              aria-label="Back"
              onClick={onBack}
              data-statsig-name="canvas-modal-back"
            >
              <ChevronLeft />
            </Button>
          )}
          {/* Decorative, so it takes the glyph scale of the controls beside it
              rather than a control box of its own. */}
          {Icon && <Icon className="size-5 shrink-0 text-muted-foreground md:size-4" />}
          <DialogTitle className={cn(panelHeaderCrumbTitleClass, titleClassName)}>
            {title}
          </DialogTitle>
          {/* Trailing action group, right-aligned as a unit so the close button
              stays flush whether or not the pager and the fullscreen toggle
              render. */}
          <div className={cn("ml-auto flex shrink-0 items-center gap-0.5", panelTrailingIconClass)}>
            {pagination && <CanvasPager {...pagination} />}
            {barActions.map((a) => (
              <PanelHeaderActionButton key={a.key} action={a} />
            ))}
            {soleAction && <PanelHeaderActionButton action={soleAction} hideBelowTier={false} />}
            {headerExtras}
            {/* With the entity's own actions rather than the frame's: a card
                covers the header that would otherwise hold these, so it has to
                carry them or they sit behind its scrim. On a desktop canvas it
                is the rightmost thing on screen; on a phone it fills the screen;
                and inside a panel's own canvas it covers that panel's header.

                Only while it is up, though — a card under another and a card in
                a collapsed column are both still mounted, and the layer is null
                for them. Which layer it is comes from the host, since that is
                what decides the header being covered. */}
            {headerUtilityLayer && <HeaderUtilitySlot layer={headerUtilityLayer} />}
            {/* Ahead of the frame's own controls, so fullscreen and close stay in
                the same spot on every card whatever the entity contributes. */}
            <PanelHeaderMenu
              title={titleText ?? (typeof title === "string" ? title : undefined)}
              label="Open card actions menu"
              show={showMenu}
              triggerRef={menuTriggerRef}
              statsigName="canvas-modal-menu"
            >
              {menuActions.map((a) => (
                <PanelHeaderMenuItem key={a.key} action={a} />
              ))}
            </PanelHeaderMenu>
            {setDisplayMode && (
              <FullscreenToggle
                isFullscreen={isFullscreen}
                onToggle={() => setDisplayMode(isFullscreen ? "card" : "full")}
              />
            )}
            {/* On a surface where this card *is* the column — filling it, with
                nothing behind it — dismissing it collapses that column, so the
                control is the column's own toggle, the one the page header
                carries for the sidebar on the other side. Elsewhere the card
                always sits over something that stays, and an X is what dismisses
                a thing sitting over something else. */}
            {dismissKind === "collapse" ? (
              <CanvasToggleButton open onToggle={onClose} />
            ) : (
              <DialogClose asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={panelHeaderControlClass}
                  aria-label="Close"
                  data-statsig-name="canvas-modal-close"
                >
                  <X />
                </Button>
              </DialogClose>
            )}
          </div>
        </SplitPanelHeader>
        {/* `flex flex-col` so a `flex-1` empty-state body still centers, while a
            comm/artifact body scrolls within the frame — whichever height the
            frame took, the capped card's or the filled column's. */}
        <div
          className={cn(
            "@container flex flex-1 min-h-0 flex-col",
            bodyFills ? "overflow-hidden" : "overflow-y-auto",
          )}
        >
          {children}
          {/* Inside the scroller so the actions belong to what they act on:
              under a short draft they sit directly below its last line, rather
              than floating at the bottom of the column where they read as the
              frame's chrome instead of this card's own Send.

              They scroll with it rather than pinning to the bottom edge, which
              is deliberate: the rich-text editor's own formatting toolbar
              already sticks there, and two bars claiming one edge means the
              later one simply paints over the other. Reserving the space is not
              available either, since this bar's height changes as its buttons
              wrap.

              The separator is an inset element rather than a `border-t`, so it
              reads as a break in the content rather than a seam across the card.
              It takes the panel gutter, as the row beneath it does, so the rule
              and the actions line up with the header at the top of the card:
              one gutter down the whole card, rather than each end keeping its
              own. Its gap below matches the body's trailing padding above, so
              the rule sits evenly between the content and the row acting on it. */}
          {footer && (
            <div className="flex shrink-0 flex-col gap-4 pb-4">
              <div className="mx-(--panel-gutter) h-px bg-border" />
              <div className="flex flex-col-reverse gap-2 px-(--panel-gutter) md:flex-row md:items-center md:justify-between">
                {footer}
              </div>
            </div>
          )}
        </div>
      </DialogContentContained>
    </Dialog>
  );
};
